import React, {useEffect} from "react"
import {
    Autocomplete, FormControl, TextField, Typography,
} from "@mui/material"
import Grid from "@mui/material/Grid2"
import {useSelector, useDispatch} from "react-redux"

import {billingApiActions} from "../../../actions"
import {
    setName,
    setEmail,
    setPhone,
    setCompany,
    setStreet,
    setZip,
    setCity
} from "../../../features/project/billingSlice"
import InfoIcon from "@mui/icons-material/Info"
import {CityItem} from "../City"
import {isValidEmail} from "../../../validation/form/emailValidation";

const PresetSelectGridItem = () => {
    const dispatch = useDispatch()
    const billings = useSelector((state: any) => state.billings)

    const toOptions = (billings: any) => Array.isArray(billings) ? billings.map(b => {
        return {
            label: `${b.company}, ${b.name}, ${b.street}, ${b.zip} ${b.city}`, value: b.id
        }
    }) : []

    const handleInputChange = (_event: any, value: any) => {
        dispatch<any>(billingApiActions.readBillings(5, value))
    }

    const handleChange = (_event: any, value: any) => {
        if (!value) {
            return
        }
        const address = billings.find((b: any) => b.id === value.value)

        if (!!address) {
            dispatch(setName(address.name))
            dispatch(setEmail(address.email))
            dispatch(setPhone(address.phone))
            dispatch(setCompany(address.company))
            dispatch(setStreet(address.street))
            dispatch(setZip(address.zip))
            dispatch(setCity(address.city))
        }
    }

    useEffect(() => {
        dispatch<any>(billingApiActions.readBillings())
    }, [dispatch])

    return (<Grid size={12}>
            <FormControl fullWidth variant="outlined">
                <Autocomplete
                    options={toOptions(billings)}
                    getOptionLabel={option => option.label.toString()}
                    renderInput={params => (<TextField
                            {...params}
                            label="Suchbegriff eingeben..."
                            variant="outlined"
                            fullWidth
                        />)}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                />
            </FormControl>
        </Grid>)
}

const LoginForPresetsTextGridItem = () => {
    return (<Grid size={12}>
            <Grid direction={"row"} container alignItems={"center"}>
                <Grid>
                    <InfoIcon style={{marginRight: "1em"}}/>
                </Grid>
                <Grid size={10}>
                    <Typography component="p" variant="body1">
                        Sie müssen angemeldet sein um Angaben aus bereits erfassten
                        Rechnungsadressen zu übernehmen.
                    </Typography>
                </Grid>
            </Grid>
        </Grid>)
}

const PresetItem = () => {
    const authentication = useSelector((state: any) => state.authentication)

    return (<Grid container spacing={2}>
            {!!authentication.loggedIn && <PresetSelectGridItem/>}
            {!authentication.loggedIn && <LoginForPresetsTextGridItem/>}
        </Grid>)
}


const NameItem = () => {
    const dispatch = useDispatch()
    const name = useSelector((state: any) => state.project.billing.name)

    return (<TextField
            margin="normal"
            variant="outlined"
            fullWidth
            id="name"
            label="Vor- und Nachname"
            name="name"
            value={name ?? ""}
            error={!name}
            helperText={!name ? "Name ist erforderlich" : null}
            onChange={e => {
                dispatch(setName(e.target.value))
            }}
        />)
}

const EMailItem = () => {
    const dispatch = useDispatch()
    const email = useSelector((state: any) => state.project.billing.email)

    return (<TextField
            margin="normal"
            variant="outlined"
            fullWidth
            id="email"
            label="E-Mail"
            name="email"
            value={email ?? ""}
            error={!email || !isValidEmail(email)}
            helperText={!email ? "E-Mail ist erforderlich" : !isValidEmail(email) ? "E-Mail ist ungültig" : null}
            onChange={e => {
                dispatch(setEmail(e.target.value))
            }}
        />)
}

const PhoneItem = () => {
    const dispatch = useDispatch()
    const phone = useSelector((state: any) => state.project.billing.phone)

    return (<TextField
            margin="normal"
            variant="outlined"
            fullWidth
            sx={{fontStyle: "italic"}}
            id="phone"
            label="Telefon"
            name="phone"
            value={phone ?? ""}
            onChange={e => {
                dispatch(setPhone(e.target.value))
            }}
        />)
}

const CompanyItem = () => {
    const dispatch = useDispatch()
    const company = useSelector((state: any) => state.project.billing.company)

    return (<TextField
            margin="normal"
            variant="outlined"
            fullWidth
            id="company"
            label="Firma"
            name="company"
            value={company ?? ""}
            error={!company}
            helperText={!company ? "Firma ist erforderlich" : null}
            onChange={e => {
                dispatch(setCompany(e.target.value))
            }}
        />)
}

const StreetItem = () => {
    const dispatch = useDispatch()
    const street = useSelector((state: any) => state.project.billing.street)

    return (<TextField
            margin="normal"
            variant="outlined"
            fullWidth
            id="street"
            label="Strasse und Nummer"
            name="street"
            value={street ?? ""}
            error={!street}
            helperText={!street ? "Strasse ist erforderlich" : null}
            onChange={e => {
                dispatch(setStreet(e.target.value))
            }}
        />)
}

export const AlternativeBillingAddressSection = () => (<Grid container spacing={6} alignItems="flex-end">
        <Grid size={6}>
            <Grid container>
                <Grid size={12} key={"upper_text"}>
                    <Typography component="p" variant="body1">
                        Wählen Sie eine bereits erfasste Rechnungsadresse aus, um die Angaben zu
                        übernehmen.
                    </Typography>
                </Grid>
                <Grid size={12} mt={2} mb={4}>
                    <PresetItem/>
                </Grid>

                <Grid size={12}>
                    <NameItem/>
                </Grid>
                <Grid size={12}>
                    <EMailItem/>
                </Grid>
                <Grid size={12}>
                    <PhoneItem/>
                </Grid>
            </Grid>
        </Grid>

        <Grid size={6}>
            <Grid container>
                <Grid size={12}>
                    <CompanyItem/>
                </Grid>
                <Grid size={12}>
                    <StreetItem/>
                </Grid>
                <Grid size={12}>
                    <CityItem
                        setZipAction={setZip} setCityAction={setCity}
                        selectZip={(state: any) => state.project.billing.zip}
                        selectCity={(state: any) => state.project.billing.city}
                    />
                </Grid>
            </Grid>

        </Grid>

    </Grid>)
